/**
 * Room — the active game room.
 *
 * Features:
 *  - WebSocket lifecycle (connect, reconnect, heartbeat)
 *  - Player roster with connection status
 *  - Room code sharing (copy on web, native share sheet in the app WebView)
 *  - Game area (lazy-loaded per game)
 *  - Winner display on game over
 *  - Instant rematch (host creates; others get one-tap rejoin)
 *  - Group leaderboard with period filter
 */

import React, { useEffect, useRef, useState, Suspense, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { ServerMessage } from '@playground/shared';
import { Avatar, Button, Card, Modal } from '@playground/ui';
import { usePlayerStore } from '@/store/player.js';
import { useRoomStore } from '@/store/room.js';
import { PlaygroundSocket } from '@/platform/socket.js';
import { getGameComponent, getGameMeta, ensureUserGameRegistered } from '@/platform/game-registry.js';
import { GameBoundary } from '@/components/GameBoundary.js';
import { subscribeToPush, clearBadge } from '@/platform/push.js';
import { isEmbedMode } from '@/platform/embed.js';
import type { GameOptionDef } from '@playground/game-sdk';
import { apiFetch, API_BASE } from '@/platform/api.js';
// `GLOBAL_GAMES` are the solo/betting tables that show session P&L rather than
// a head-to-head leaderboard.
import { GLOBAL_GAMES as SOLO_GAMES, isSelfContainedGame } from '@/platform/games.js';
import ProviderIntro from '@/platform/provider/ProviderIntro';
import { accentFor } from '@/platform/provider/brand';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Period = 'today' | '7d' | '30d' | 'all';

interface ChatMsg {
  playerId: string;
  displayName: string;
  text: string;
  ts: number;
}

interface LeaderboardRow {
  playerId: string;
  displayName: string;
  wins: number;
  losses: number;
  draws: number;
}

// ---------------------------------------------------------------------------
// Leaderboard component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Session stats — shown for global solo-betting tables (bubble, moka, cards)
// ---------------------------------------------------------------------------

function SessionStats({ gameState }: { gameState: unknown }) {
  if (!gameState || typeof gameState !== 'object') return null;
  const state = gameState as {
    totalProfit?: Record<string, number>;
    displayNames?: Record<string, string>;
    playerOrder?: string[];
  };
  const { totalProfit = {}, displayNames = {}, playerOrder = [] } = state;
  const ranked = playerOrder
    .filter((id) => id in displayNames && (totalProfit[id] ?? 0) !== 0)
    .sort((a, b) => (totalProfit[b] ?? 0) - (totalProfit[a] ?? 0));
  if (ranked.length === 0) return null;

  return (
    <Card className="mt-4">
      <div className="mb-3">
        <p className="font-semibold text-gray-900 dark:text-gray-100">Session</p>
        <p className="text-xs text-gray-500">Profit / loss in this session</p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-left text-xs text-gray-500 dark:text-gray-400 dark:border-gray-700">
            <th className="pb-2 font-medium">#</th>
            <th className="pb-2 font-medium">Player</th>
            <th className="pb-2 text-right font-medium">Net</th>
          </tr>
        </thead>
        <tbody>
          {ranked.map((id, i) => {
            const net = totalProfit[id] ?? 0;
            return (
              <tr key={id} className="border-b border-gray-50 last:border-0 dark:border-gray-700">
                <td className="py-2 text-gray-500 dark:text-gray-400 w-6">{i + 1}</td>
                {/* Display names are free text — without a cap a long one pushes
                    the Net column off a phone screen. */}
                <td
                  title={displayNames[id] ?? id}
                  className="max-w-[8rem] truncate py-2 pr-2 font-medium text-gray-900 dark:text-gray-100"
                >
                  {displayNames[id] ?? id}
                </td>
                <td className={`py-2 text-right font-semibold tabular-nums ${net >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {net >= 0 ? '+' : ''}{net.toFixed(2)} cr
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

function GroupLeaderboard({ roomId, playerCount, status }: { roomId: string; playerCount: number; status: string }) {
  const [period, setPeriod] = useState<Period>('all');
  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(false);

  const periods: { value: Period; label: string }[] = [
    { value: 'today', label: 'Today' },
    { value: '7d', label: '7 days' },
    { value: '30d', label: '30 days' },
    { value: 'all', label: 'All time' },
  ];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch(`/api/leaderboard/room/${roomId}?period=${period}`)
      .then((r) => (r.ok ? r.json() : null))
      // `data.rows` is undefined whenever the endpoint answers with an error
      // body; setting that into state made the next render throw on `.length`.
      .then((data: { rows?: LeaderboardRow[] } | null) => {
        if (!cancelled) setRows(data?.rows ?? []);
      })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [roomId, period, playerCount, status]);

  if (rows.length === 0 && !loading) return null;

  return (
    <Card className="mt-4">
      <div className="mb-3">
        <p className="font-semibold text-gray-900 dark:text-gray-100">Head-to-Head</p>
        <p className="text-xs text-gray-500">Stats between players in this room</p>
      </div>

      {/* Period tabs */}
      <div className="mb-4 flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-700">
        {periods.map((p) => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            className={[
              // py-2 for the same reason as the Leaderboard row: py-1 leaves
              // these at 24px, the floor for a tap target.
              'flex-1 rounded-md px-2 py-2 text-xs font-medium transition-colors',
              period === p.value
                ? 'bg-white text-indigo-700 shadow-sm dark:bg-gray-600 dark:text-indigo-200'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
            ].join(' ')}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs text-gray-500 dark:text-gray-400 dark:border-gray-700">
              <th className="pb-2 font-medium">Player</th>
              <th className="pb-2 text-center font-medium text-green-600">W</th>
              <th className="pb-2 text-center font-medium text-red-500">L</th>
              <th className="pb-2 text-center font-medium text-gray-500 dark:text-gray-400">D</th>
            </tr>
          </thead>
          <tbody>
            {rows
              .slice()
              .sort((a, b) => b.wins - a.wins || a.losses - b.losses)
              .map((row) => (
                <tr key={row.playerId} className="border-b border-gray-50 last:border-0 dark:border-gray-700">
                  <td
                    title={row.displayName}
                    className="max-w-[9rem] truncate py-2 pr-2 font-medium text-gray-900 dark:text-gray-100"
                  >
                    {row.displayName}
                  </td>
                  <td className="py-2 text-center font-semibold text-green-600">{row.wins}</td>
                  <td className="py-2 text-center text-red-500">{row.losses}</td>
                  <td className="py-2 text-center text-gray-500 dark:text-gray-400">{row.draws}</td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Clipboard helper
// ---------------------------------------------------------------------------

/**
 * Copies text and reports whether it landed. `navigator.clipboard` rejects on
 * insecure origins and when the user denies permission, so callers must not
 * assume success.
 */
// ---------------------------------------------------------------------------
// Bespoke overlay plumbing
// ---------------------------------------------------------------------------

/**
 * Open hand-rolled overlays, innermost last.
 *
 * Two things need this to be shared rather than per-overlay, because more than
 * one of these can be open at once — the how-to-play sheet auto-opens the first
 * time you enter a room for a game, and chat opens on top of it:
 *
 *  - Escape must dismiss only the topmost one. Every overlay listening on
 *    `window` in the capture phase means `stopPropagation` can't help
 *    (listeners on the same target in the same phase all run), so the topmost
 *    entry is the only one allowed to act.
 *  - The scroll lock has to be reference counted. Each overlay capturing and
 *    restoring `body.style.overflow` on its own meant the second one captured
 *    "hidden" from the first and restored *that* on close — leaving the page
 *    permanently unscrollable once two had been opened together.
 */
const openOverlays: Array<() => void> = [];
let scrollLockCount = 0;
let scrollLockPrevious = '';

function lockBodyScroll(): void {
  if (scrollLockCount === 0) {
    scrollLockPrevious = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLockCount++;
}

function unlockBodyScroll(): void {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) document.body.style.overflow = scrollLockPrevious;
}

/**
 * Escape-to-dismiss and body scroll locking for the bespoke overlays below.
 *
 * The invite sheet, details and leave dialogs go through `Modal`, which gets
 * both from the native <dialog>. The chat sheet, how-to-play and exit-confirm
 * overlays are hand-rolled bottom sheets — they had neither, so Escape did
 * nothing and the page kept scrolling under a finger dragging on the sheet.
 * Same contract as `Modal`, without restyling them.
 *
 * `escape: false` locks scrolling only. The fullscreen game overlay uses that:
 * it hosts a live game that owns the keyboard, and swallowing Escape there
 * would tear down the frame out from under whatever dialog the game itself had
 * open. It closes from its own visible button instead.
 */
function useOverlayDismiss(open: boolean, onClose: () => void, opts?: { escape?: boolean }): void {
  const escape = opts?.escape ?? true;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const entry = () => closeRef.current();
    if (escape) openOverlays.push(entry);

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A native <dialog> sits above these (z-[100] vs z-50) and closes itself
      // on Escape; while one is up it owns the key.
      if (document.querySelector('dialog[open]')) return;
      if (openOverlays[openOverlays.length - 1] !== entry) return;
      // Topmost: nothing below should also act on this keypress.
      e.stopPropagation();
      entry();
    };

    if (escape) window.addEventListener('keydown', onKey, true);
    lockBodyScroll();

    return () => {
      if (escape) {
        window.removeEventListener('keydown', onKey, true);
        const i = openOverlays.lastIndexOf(entry);
        if (i !== -1) openOverlays.splice(i, 1);
      }
      unlockBodyScroll();
    };
  }, [open, escape]);
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Invite sheet
// ---------------------------------------------------------------------------

/** Deep link that drops whoever opens it straight into this room. */
function joinLink(roomId: string): string {
  return `${window.location.origin}/join?code=${encodeURIComponent(roomId)}`;
}

/**
 * Opens a URL that leaves the app (WhatsApp, the SMS composer). `window.open`
 * returns null when a WebView blocks the popup, so fall back to navigating.
 */
function openExternal(url: string): void {
  const w = window.open(url, '_blank', 'noopener,noreferrer');
  if (!w) window.location.href = url;
}

interface InviteSheetProps {
  open: boolean;
  onClose: () => void;
  roomId: string;
  gameName: string;
  /** `game` = dark battle chrome (embed lobby); `app` = themed app card. */
  tone: 'game' | 'app';
}

/**
 * Invite popup behind the share button.
 *
 * The button used to post REQUEST_SHARE straight at the native host and do
 * nothing else. Outside the app — and inside hosts that don't handle that
 * message — the call is a silent no-op, so the button looked dead. Every
 * action here works on its own; the native sheet is only the first thing
 * "Share invite" reaches for.
 */
function InviteSheet({ open, onClose, roomId, gameName, tone }: InviteSheetProps) {
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [shareNote, setShareNote] = useState<string | null>(null);
  const dark = tone === 'game';

  const link = joinLink(roomId);
  const message = `Join my ${gameName} game — room code ${roomId}\n${link}`;

  function flash(what: 'code' | 'link') {
    setCopied(what);
    setTimeout(() => setCopied(null), 2000);
  }

  async function copy(text: string, what: 'code' | 'link') {
    if (await copyText(text)) flash(what);
    else setShareNote('Copying is blocked here — select the text manually to share it.');
  }

  /** Native app sheet → Web Share API → clipboard, whichever exists first. */
  async function share() {
    setShareNote(null);
    const bridge = window.ReactNativeWebView;
    if (bridge) {
      // Older hosts only read `roomId`; the extra fields are ignored there.
      bridge.postMessage(JSON.stringify({ type: 'REQUEST_SHARE', roomId, url: link, text: message }));
      return;
    }
    if (typeof navigator.share === 'function') {
      // A rejection here is either the user dismissing the sheet or the browser
      // refusing the payload. Either way the popup stays open to copy from.
      try {
        await navigator.share({ title: gameName, text: message, url: link });
      } catch { /* dismissed */ }
      return;
    }
    if (await copyText(link)) {
      flash('link');
      setShareNote('Invite link copied — paste it to your friends.');
    } else {
      setShareNote('Copying is blocked here — select the link manually to share it.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invite players"
      placement={dark ? 'sheet' : 'center'}
      panelClassName={
        dark
          ? 'w-full max-w-sm overflow-hidden rounded-3xl'
          : 'w-full max-w-sm rounded-2xl bg-white shadow-xl dark:bg-gray-800'
      }
      {...(dark ? { panelStyle: { background: 'linear-gradient(to bottom, #0D1520, #111827)' } } : {})}
    >
      <div className="flex flex-col gap-5 px-6 pb-7 pt-6">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${dark ? 'bg-cyan-400/10' : 'bg-indigo-100 dark:bg-indigo-900/40'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${dark ? 'text-cyan-400' : 'text-indigo-600 dark:text-indigo-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/>
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={`text-base font-black uppercase tracking-wide ${dark ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>Invite players</h3>
            <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">{gameName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-all active:scale-95 ${dark ? 'bg-gray-700/60 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-900 dark:bg-gray-700 dark:hover:text-white'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Room code — the whole tile copies */}
        <button
          type="button"
          onClick={() => void copy(roomId, 'code')}
          aria-label={`Room code ${roomId}. Copy to clipboard.`}
          className={`flex w-full flex-col items-center gap-1 rounded-2xl border px-4 py-4 transition-all active:scale-[0.98] ${dark ? 'border-cyan-400/30 bg-cyan-400/5 hover:bg-cyan-400/10' : 'border-gray-200 bg-gray-50 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700/40 dark:hover:bg-gray-700'}`}
        >
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
            {copied === 'code' ? '✓ Code copied' : 'Room code — tap to copy'}
          </span>
          <span className={`font-mono text-3xl font-black tracking-[0.2em] ${dark ? 'text-cyan-400' : 'text-indigo-600 dark:text-indigo-400'}`}>{roomId}</span>
        </button>

        {/* Join link */}
        <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${dark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-700/40'}`}>
          <span className={`min-w-0 flex-1 truncate text-xs ${dark ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>{link}</span>
          <button
            type="button"
            onClick={() => void copy(link, 'link')}
            className={`shrink-0 rounded-lg px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-all active:scale-95 ${dark ? 'bg-gray-700 text-gray-300 hover:text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200'}`}
          >
            {copied === 'link' ? 'Copied' : 'Copy'}
          </button>
        </div>

        {/* Share — the native sheet where there is one, clipboard where there isn't */}
        <button
          type="button"
          onClick={() => void share()}
          className={`flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-black uppercase tracking-widest transition-transform active:scale-[0.97] ${dark ? 'bg-cyan-400 text-black' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          Share invite
        </button>

        {/* Direct invites */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => openExternal(`https://wa.me/?text=${encodeURIComponent(message)}`)}
            className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 text-xs font-bold uppercase tracking-wide transition-all active:scale-95 ${dark ? 'border-gray-700 bg-gray-800/60 text-gray-300 hover:text-white' : 'border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.04 2a9.9 9.9 0 0 0-9.89 9.9 9.8 9.8 0 0 0 1.33 4.95L2 22l5.29-1.38a9.9 9.9 0 0 0 4.75 1.21 9.9 9.9 0 0 0 9.89-9.9A9.9 9.9 0 0 0 12.04 2m0 18.13a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.13.82.84-3.06-.2-.31a8.2 8.2 0 0 1-1.26-4.36 8.23 8.23 0 0 1 14.05-5.82 8.2 8.2 0 0 1 2.42 5.83 8.23 8.23 0 0 1-8.23 8.23m4.52-6.16c-.25-.13-1.47-.72-1.69-.8s-.4-.13-.56.12-.64.8-.79.97-.29.18-.54.06a6.7 6.7 0 0 1-1.99-1.23 7.5 7.5 0 0 1-1.38-1.71c-.14-.25-.01-.38.11-.5s.25-.29.37-.44a1.7 1.7 0 0 0 .25-.41.46.46 0 0 0-.02-.44c-.06-.12-.55-1.34-.76-1.83s-.4-.42-.55-.43h-.47a.9.9 0 0 0-.65.31 2.75 2.75 0 0 0-.86 2.04 4.77 4.77 0 0 0 1 2.53 10.9 10.9 0 0 0 4.18 3.7 14 14 0 0 0 1.4.51 3.35 3.35 0 0 0 1.54.1 2.53 2.53 0 0 0 1.65-1.16 2.04 2.04 0 0 0 .14-1.16c-.06-.1-.22-.16-.47-.28"/>
            </svg>
            WhatsApp
          </button>
          <button
            type="button"
            onClick={() => openExternal(`sms:?&body=${encodeURIComponent(message)}`)}
            className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 text-xs font-bold uppercase tracking-wide transition-all active:scale-95 ${dark ? 'border-gray-700 bg-gray-800/60 text-gray-300 hover:text-white' : 'border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${dark ? 'text-cyan-400' : 'text-indigo-500'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            Message
          </button>
        </div>

        {shareNote && (
          <p className="text-center text-[11px] text-gray-500 dark:text-gray-400">{shareNote}</p>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Rules modal
// ---------------------------------------------------------------------------

function DetailsModal({
  open, gameId, gameName, isHost, gameOptions, onOptionsChange, onClose,
}: {
  open: boolean;
  gameId: string;
  gameName: string;
  isHost: boolean;
  gameOptions: Record<string, unknown>;
  onOptionsChange: (opts: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const meta = getGameMeta(gameId);
  const rules = meta?.howToPlay ?? ['No rules available for this game.'];
  const optionDefs: readonly GameOptionDef[] = meta?.options ?? [];
  const [tab, setTab] = React.useState<'rules' | 'settings'>(optionDefs.length > 0 ? 'settings' : 'rules');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${gameName} details`}
      panelClassName="flex max-h-[90vh] w-full max-w-sm flex-col rounded-2xl bg-white shadow-xl dark:bg-gray-800"
    >
      <div className="border-b border-gray-100 px-6 pb-3 pt-5 dark:border-gray-700">
        <h3 className="text-center text-base font-bold text-gray-900 dark:text-gray-100">{gameName}</h3>
      </div>

      {/* Tabs */}
      {optionDefs.length > 0 && (
        <div role="tablist" aria-label="Game details" className="flex border-b border-gray-100 dark:border-gray-700">
          {(['settings', 'rules'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              id={`details-tab-${t}`}
              aria-selected={tab === t}
              aria-controls={`details-panel-${t}`}
              onClick={() => setTab(t)}
              className={[
                'flex-1 py-2.5 text-sm font-medium transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500',
                tab === t
                  ? 'border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400',
              ].join(' ')}
            >
              <span aria-hidden="true">{t === 'settings' ? '⚙️' : '📖'}</span>{' '}
              {t === 'settings' ? 'Settings' : 'How to Play'}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {tab === 'rules' || optionDefs.length === 0 ? (
          <ol
            id="details-panel-rules"
            role={optionDefs.length > 0 ? 'tabpanel' : undefined}
            aria-labelledby={optionDefs.length > 0 ? 'details-tab-rules' : undefined}
            className="space-y-2"
          >
            {rules.map((rule, i) => (
              <li key={i} className="flex gap-2 text-sm">
                <span aria-hidden="true" className="mt-0.5 shrink-0 font-bold text-indigo-500">{i + 1}.</span>
                <span className="text-pretty text-gray-700 dark:text-gray-300">{rule}</span>
              </li>
            ))}
          </ol>
        ) : (
          <div
            id="details-panel-settings"
            role="tabpanel"
            aria-labelledby="details-tab-settings"
            className="space-y-4"
          >
            {!isHost && (
              <p className="text-center text-xs text-gray-500 dark:text-gray-400">Only the host can change settings.</p>
            )}
            {optionDefs.map((opt) => {
              const value = gameOptions[opt.key] ?? opt.default;
              const fieldId = `opt-${opt.key}`;
              return (
                <div key={opt.key}>
                  {opt.type === 'number' ? (
                    <label htmlFor={fieldId} className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {opt.label}
                    </label>
                  ) : (
                    <p id={`${fieldId}-label`} className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {opt.label}
                    </p>
                  )}
                  {opt.type === 'select' && (
                    <div role="group" aria-labelledby={`${fieldId}-label`} className="flex flex-wrap gap-2">
                      {opt.choices.map((c: { value: string | number; label: string }) => (
                        <button
                          key={String(c.value)}
                          type="button"
                          disabled={!isHost}
                          aria-pressed={value === c.value}
                          onClick={() => isHost && onOptionsChange({ ...gameOptions, [opt.key]: c.value })}
                          className={[
                            'rounded-lg border-2 px-3 py-1.5 text-sm font-semibold transition-colors',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                            value === c.value
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                              : 'border-gray-200 text-gray-600 dark:border-gray-600 dark:text-gray-400',
                            isHost ? 'cursor-pointer hover:border-indigo-300' : 'cursor-default opacity-70',
                          ].join(' ')}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {opt.type === 'number' && (
                    <div className="flex items-center gap-2">
                      <input
                        id={fieldId}
                        type="number"
                        disabled={!isHost}
                        min={opt.min}
                        max={opt.max}
                        step={opt.step ?? 1}
                        value={Number(value)}
                        onChange={(e) => isHost && onOptionsChange({ ...gameOptions, [opt.key]: Number(e.target.value) })}
                        className="w-24 rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm tabular-nums text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      />
                      {opt.unit && <span className="text-sm text-gray-500 dark:text-gray-400">{opt.unit}</span>}
                    </div>
                  )}
                  {opt.type === 'boolean' && (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={Boolean(value)}
                      aria-labelledby={`${fieldId}-label`}
                      disabled={!isHost}
                      onClick={() => isHost && onOptionsChange({ ...gameOptions, [opt.key]: !value })}
                      className={[
                        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
                        value ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600',
                        isHost ? 'cursor-pointer' : 'cursor-default opacity-70',
                      ].join(' ')}
                    >
                      <span
                        aria-hidden="true"
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-150 ease-out ${value ? 'translate-x-6' : 'translate-x-1'}`}
                      />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 px-6 pb-5 pt-3 dark:border-gray-700">
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-lg bg-gray-100 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Chat helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(ts: number): string {
  const diffSec = Math.floor((Date.now() - ts) / 1000);
  if (diffSec < 10) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  return `${Math.floor(diffMin / 60)}h ago`;
}

interface ChatBottomSheetProps {
  showChat: boolean;
  onClose: () => void;
  chatMessages: ChatMsg[];
  chatInput: string;
  setChatInput: (v: string) => void;
  handleSendChat: (e: React.FormEvent) => void;
  chatEndRef: React.RefObject<HTMLDivElement>;
  chatInputRef: React.RefObject<HTMLInputElement>;
  playerId: string;
}

function ChatBottomSheet({
  showChat, onClose, chatMessages, chatInput, setChatInput,
  handleSendChat, chatEndRef, chatInputRef, playerId,
}: ChatBottomSheetProps) {
  useOverlayDismiss(showChat, onClose);
  if (!showChat) return null;

  const colors = ['from-cyan-500 to-blue-600', 'from-violet-500 to-purple-700', 'from-pink-500 to-rose-600', 'from-amber-500 to-orange-600'];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm p-4 pb-8"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-3xl flex flex-col overflow-hidden"
        style={{ background: 'linear-gradient(to bottom, #0D1520, #111827)', maxHeight: '80vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-cyan-400/10">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <h3 className="text-base font-black uppercase tracking-wide text-white">Chat</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-700/60 text-gray-400 hover:text-white active:scale-95 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-700/60 mx-6 shrink-0" />

        {/* Messages */}
        <div className="overflow-y-auto px-5 py-4 space-y-3" style={{ maxHeight: '50vh' }}>
          {chatMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <p className="text-2xl">👋</p>
              <p className="text-xs text-gray-500 text-center">No messages yet. Say hello!</p>
            </div>
          ) : (
            chatMessages.map((m, i) => {
              const isMe = m.playerId === playerId;
              const colorIdx = m.playerId.charCodeAt(0) % colors.length;
              const initial = m.displayName?.[0]?.toUpperCase() ?? '?';
              return (
                <div key={i} className={`flex gap-2 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${colors[colorIdx]} flex items-center justify-center text-[10px] font-black text-white shrink-0 self-end`}>
                    {initial}
                  </div>
                  <div className={`flex flex-col gap-0.5 max-w-[72%] ${isMe ? 'items-end' : 'items-start'}`}>
                    <span className={`text-[10px] font-semibold uppercase tracking-wide ${isMe ? 'text-cyan-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {m.displayName}
                    </span>
                    <div className={`rounded-2xl px-3.5 py-2 text-sm leading-snug break-words border ${
                      isMe
                        ? 'bg-cyan-400/10 border-cyan-400/30 text-white rounded-tr-sm'
                        : 'bg-gray-800/60 border-gray-700 text-gray-200 rounded-tl-sm'
                    }`}>
                      {m.text}
                    </div>
                    <span className="text-[10px] text-gray-500">{formatRelativeTime(m.ts)}</span>
                  </div>
                </div>
              );
            })
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <div className="shrink-0 px-5 pb-6 pt-3 border-t border-gray-700/60">
          <form onSubmit={handleSendChat} className="flex items-center gap-2">
            <input
              ref={chatInputRef}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              maxLength={300}
              placeholder="Say something…"
              className="flex-1 rounded-xl bg-gray-800 border border-gray-700 focus:border-cyan-400/60 focus:outline-none px-4 py-2.5 text-sm text-white placeholder-gray-600 transition-colors"
            />
            <button
              type="submit"
              disabled={!chatInput.trim()}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-400 text-black disabled:opacity-40 active:scale-95 transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Room component
// ---------------------------------------------------------------------------

export function Room() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();

  const { playerId, displayName, avatarUrl, token, credits, setCredits, favorites, toggleFavorite } = usePlayerStore();

  const {
    gameId,
    players,
    gameState,
    status,
    winners,
    scores,
    gameOverReason,
    rematchRoomId,
    serverClockOffset,
    updateState,
    upsertPlayer,
    removePlayer,
    finishRoom,
    setRematchRoomId,
    setRoom,
    reset,
  } = useRoomStore();

  const socketRef = useRef<PlaygroundSocket | null>(null);
  // Keep a ref so the WS effect always reads the latest token without re-running
  // when token changes mid-navigation (the rematch race condition).
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const [connected, setConnected] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);
  const [startingGame, setStartingGame] = useState(false);
  const [powerUpCosts, setPowerUpCosts] = useState<Record<string, number>>({});
  const [powerUpUsesPerMatch, setPowerUpUsesPerMatch] = useState<Record<string, number>>({});
  const [userGameReady, setUserGameReady] = useState(false);
  const [powerUpUsage, setPowerUpUsage] = useState<Record<string, number>>({});
  const [gameOptions, setGameOptions] = useState<Record<string, unknown>>({});
  const [creditsEarned, setCreditsEarned] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [rematchLoading, setRematchLoading] = useState(false);
  const [joiningRematch, setJoiningRematch] = useState(false);
  const [h2hRows, setH2hRows] = useState<LeaderboardRow[]>([]);

  // The bespoke overlays. (`showChat` is wired inside ChatBottomSheet, and the
  // `Modal`-based dialogs already handle Escape and scroll locking themselves.)
  useOverlayDismiss(showRules, () => setShowRules(false));
  useOverlayDismiss(showExitConfirm, () => setShowExitConfirm(false));
  useOverlayDismiss(isFullscreen, () => setIsFullscreen(false), { escape: false });
  const [playersOpen, setPlayersOpen] = useState(true);

  // Chat
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatToast, setChatToast] = useState<ChatMsg | null>(null);
  const [chatInput, setChatInput] = useState('');
  const chatOpenRef = useRef(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Push notifications: subscribe once per session + clear badge on window focus
  useEffect(() => {
    if (playerId) void subscribeToPush(playerId);
    const handleFocus = () => clearBadge();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [playerId]);

  // Fetch head-to-head stats when the game ends (used by embed game over screen)
  useEffect(() => {
    if (status !== 'finished' || !roomId) return;
    let cancelled = false;
    apiFetch(`/api/leaderboard/room/${roomId}?period=all`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { rows?: LeaderboardRow[] } | null) => {
        if (!cancelled) setH2hRows(data?.rows ?? []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [status, roomId]);

  // Auto-show rules modal on first visit to this game type (embed mode only)
  useEffect(() => {
    if (!isEmbedMode() || !gameId || status !== 'playing') return;
    const key = `rules-seen-${gameId}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      setShowRules(true);
    }
  }, [gameId, status]);

  // Keep ref in sync so WS closure reads latest value
  useEffect(() => { chatOpenRef.current = chatOpen || showChat; }, [chatOpen, showChat]);
  // Auto-scroll chat to bottom on new messages
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);
  // Focus input when chat opens (desktop or embed)
  useEffect(() => {
    if (chatOpen) setTimeout(() => chatInputRef.current?.focus(), 50);
  }, [chatOpen]);
  // Scroll to bottom + focus input when embed chat panel opens
  useEffect(() => {
    if (showChat) {
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'instant' });
        chatInputRef.current?.focus();
      }, 50);
    }
  }, [showChat]);

  const localPlayer = players.find((p) => p.id === playerId);
  const isHost = localPlayer?.isHost ?? false;

  // Winner info
  const winnerPlayers = players.filter((p) => winners.includes(p.id));
  // winners===[] means everyone tied (RPS draw convention); winners.length>1 means multi-winner draw (TicTacToe)
  const isDraw = winners.length === 0 || winners.length > 1;
  const iWon = winners.includes(playerId);

  // ---------------------------------------------------------------------------
  // WebSocket lifecycle
  // ---------------------------------------------------------------------------

  useEffect(() => {
    // Token may arrive via URL param (e.g. opened from Builder) — read it directly
    // here to avoid the race condition where this effect runs before the
    // token-from-URL effect has had a chance to call setToken().
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    if (urlToken) {
      usePlayerStore.getState().setToken(urlToken);
      tokenRef.current = urlToken;
      const clean = new URL(window.location.href);
      clean.searchParams.delete('token');
      window.history.replaceState({}, '', clean.toString());
    }

    const currentToken = tokenRef.current;
    if (!roomId || !currentToken) {
      void navigate('/');
      return;
    }

    const socket = new PlaygroundSocket({ roomId, token: currentToken });
    socketRef.current = socket;

    socket.on('open', () => setConnected(true));
    socket.on('close', () => setConnected(false));
    socket.on('give_up', () => setGaveUp(true));

    socket.on('message', (msg: ServerMessage) => {
      switch (msg.type) {
        case 'state':
          // Fire GAME_STARTED when status transitions to 'playing'
          if (msg.payload.status === 'playing' && useRoomStore.getState().status !== 'playing') {
            window.ReactNativeWebView?.postMessage(JSON.stringify({
              type: 'GAME_STARTED',
              gameId: (msg.payload.gameId as string | undefined) ?? useRoomStore.getState().gameId,
            }));
          }
          // gameId arrives in the first state message on join — persist it to the store
          if (msg.payload.gameId && !useRoomStore.getState().gameId) {
            setRoom({
              roomId: roomId!,
              gameId: msg.payload.gameId as string,
              players: (msg.payload.players as never[] | undefined) ?? [],
              status: msg.payload.status as never,
            });
          }
          updateState({
            gameState: msg.payload.gameState,
            status: msg.payload.status,
            ...(msg.payload.players !== undefined && { players: msg.payload.players }),
            seq: msg.seq,
            serverClockOffset: msg.ts - Date.now(),
          });
          break;

        case 'player_joined':
          upsertPlayer(msg.payload.player);
          break;

        case 'player_left':
          if (msg.payload.disconnected) {
            const existing = useRoomStore.getState().players.find(
              (p) => p.id === msg.payload.playerId,
            );
            if (existing) {
              upsertPlayer({ ...existing, isConnected: false });
            }
          } else {
            removePlayer(msg.payload.playerId);
          }
          break;

        case 'game_over':
          finishRoom({
            winners: msg.payload.winners,
            scores: msg.payload.scores ?? {},
            reason: msg.payload.reason,
          });
          window.ReactNativeWebView?.postMessage(JSON.stringify({
            type: 'GAME_ENDED',
            winners: msg.payload.winners,
            scores: msg.payload.scores ?? {},
          }));
          break;

        case 'rematch':
          setRematchRoomId(msg.payload.newRoomId);
          break;

        case 'error':
          console.error('[Room] Server error:', msg.payload.message);
          break;

        case 'credits_update': {
          const cp = msg.payload as { balance: number; earned?: number };
          setCredits(cp.balance);
          if (cp.earned && cp.earned > 0) setCreditsEarned(cp.earned);
          break;
        }

        case 'chat_message': {
          const cm: ChatMsg = { playerId: msg.payload.playerId, displayName: msg.payload.displayName, text: msg.payload.text, ts: msg.ts };
          setChatMessages((prev) => [...prev, cm]);
          if (!chatOpenRef.current) {
            setUnreadCount((n) => n + 1);
            setChatToast(cm);
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            toastTimerRef.current = setTimeout(() => setChatToast(null), 3000);
          }
          break;
        }

        case 'pong':
        case 'event':
          break;
      }
    });

    socket.connect();

    const pingInterval = setInterval(() => {
      if (socket.isConnected) socket.sendPing();
    }, 15_000);

    return () => {
      clearInterval(pingInterval);
      socket.destroy();
      socketRef.current = null;
      setConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Fetch credit balance when entering the room. JWT-gated — playerId is
  // resolved server-side from the token, never from a URL param.
  useEffect(() => {
    if (!playerId || !token) return;
    apiFetch('/api/credits/me', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { balance: number } | null) => {
        if (data) setCredits(data.balance);
      })
      .catch(() => {});
  }, [playerId, token, setCredits]);

  // Ensure user-created games are registered in the frontend registry
  useEffect(() => {
    if (!gameId) return;
    ensureUserGameRegistered(gameId).then(() => setUserGameReady(true)).catch(() => {});
  }, [gameId]);

  // Initialise game options from declared defaults when gameId is known
  useEffect(() => {
    if (!gameId) return;
    const meta = getGameMeta(gameId);
    if (!meta?.options?.length) return;
    const defaults: Record<string, unknown> = {};
    for (const opt of meta.options) defaults[opt.key] = opt.default;
    setGameOptions(defaults);
  }, [gameId]);

  // Fetch power-up costs for this game.
  // Defaults to {} on any failure — `handleAction` indexes powerUpCosts on
  // every action, so an undefined map would throw on the first move.
  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    apiFetch(`/api/game-config/${gameId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { powerUpCosts?: Record<string, number>; powerUpUsesPerMatch?: Record<string, number> } | null) => {
        if (cancelled) return;
        setPowerUpCosts(data?.powerUpCosts ?? {});
        setPowerUpUsesPerMatch(data?.powerUpUsesPerMatch ?? {});
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [gameId]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  // Auto-collapse players panel when game starts; reset counters
  useEffect(() => {
    if (status === 'playing') {
      setPlayersOpen(false);
      setPowerUpUsage({});
      setCreditsEarned(null);
      setShowChat(false);
    }
    if (status === 'finished') {
      setIsFullscreen(false);
      setShowChat(false);
    }
  }, [status]);

  function handleAction(action: unknown) {
    // Track local power-up usage for button disable UX (server enforces the real limit)
    const actionType = (action as { type?: string }).type;
    if (actionType && powerUpCosts[actionType] !== undefined) {
      setPowerUpUsage((prev) => ({ ...prev, [actionType]: (prev[actionType] ?? 0) + 1 }));
    }
    socketRef.current?.sendAction(action);
  }

  async function handleStartGame() {
    if (!roomId) return;
    setStartingGame(true);
    try {
      const res = await apiFetch(`/api/rooms/${roomId}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token ?? ''}` },
        body: JSON.stringify({ options: gameOptions }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        console.error('Failed to start game:', body.message);
      }
    } finally {
      setStartingGame(false);
    }
  }

  async function handleRematch() {
    if (!roomId) return;
    setRematchLoading(true);
    try {
      const res = await apiFetch(`/api/rooms/${roomId}/rematch`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token ?? ''}` },
      });
      if (!res.ok) return;
      const data = (await res.json()) as { newRoomId: string; token: string };
      // Update store for the new room
      const { setToken } = usePlayerStore.getState();
      setToken(data.token);
      setRoom({
        roomId: data.newRoomId,
        gameId: gameId ?? '',
        players: [],
        status: 'waiting',
      });
      void navigate(`/room/${data.newRoomId}`);
    } finally {
      setRematchLoading(false);
    }
  }

  async function handleJoinRematch() {
    if (!rematchRoomId) return;
    setJoiningRematch(true);
    try {
      // displayName may be empty if the player entered via a ?token= URL (Builder flow).
      // Fall back to the name already known in this room's player list.
      const effectiveName =
        displayName.trim() ||
        players.find((p) => p.id === playerId)?.displayName ||
        'Player';
      const res = await apiFetch(`/api/rooms/${rematchRoomId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ playerId, displayName: effectiveName }),
      });
      if (!res.ok) return;
      const data = (await res.json()) as { token: string; gameId: string; players: unknown[] };
      const { setToken } = usePlayerStore.getState();
      setToken(data.token);
      setRoom({
        roomId: rematchRoomId,
        gameId: data.gameId,
        players: data.players as never,
        status: 'waiting',
      });
      void navigate(`/room/${rematchRoomId}`);
    } finally {
      setJoiningRematch(false);
    }
  }

  function handleLeave() {
    socketRef.current?.destroy();
    reset();
    void navigate('/');
  }

  function handleSendChat(e: React.FormEvent) {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text || !socketRef.current?.isConnected) return;
    try { socketRef.current.send('chat', { text }); } catch { return; }
    setChatInput('');
  }

  // ---------------------------------------------------------------------------
  // Guard
  // ---------------------------------------------------------------------------

  if (!roomId) return null;

  const embed = isEmbedMode();

  // Re-evaluated after userGameReady flips to true (ensures user games registered via ensureUserGameRegistered are picked up)
  const GameComponent = gameId ? getGameComponent(gameId) : null;
  const gameMeta = gameId ? getGameMeta(gameId) : null;
  const gameName = gameMeta?.name ?? gameId ?? 'Room';

  // Horse Racing, Car Racing, Aether Dynasty and Sky Crash mount their full
  // standalone SPAs (own splash → game flow). Give them the entire screen with no
  // playground chrome so they open on their own home screen and run their normal
  // procedure.
  if (gameMeta && isSelfContainedGame(gameMeta.id) && GameComponent) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: '#05080f' }}>
        {/*
          The provider title sequence, and the "Powered by" credit that stays on
          screen for the whole session. It WRAPS the game rather than gating it:
          the game mounts and boots underneath while the mark is up, so the ~3.5s
          costs almost nothing in real time.

          It lives here rather than in the nine packages because there is exactly
          one place a self-contained game is mounted, and one copy of a 37KB
          three.js sequence is better than nine vendored ones. The games reach
          the other half of it — "the screen has been handed over, start the
          music" — through their own vendored `onIntroDone`, which tests the DOM
          rather than importing anything from here.

          ONE DIFFERENCE FROM THE SOURCE APP, WHICH IS A ROUTE CHANGE THERE AND A
          COMPONENT SWAP HERE: `window.load` has long since fired by the time a
          player reaches a room, so the sequence always runs to its floor
          (INTRO_MIN_MS) rather than to a real readiness signal. That is the
          intended length anyway — the ceiling is what the load event was ever
          able to save — and a game whose chunk is still arriving at the cut has
          its own loading screen behind this one.
        */}
        <ProviderIntro
          gameId={gameMeta.id}
          accent={accentFor(gameMeta.id)}
          title={gameName}
        >
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-cyan-400/30 border-t-cyan-400" />
              </div>
            }
          >
            <GameBoundary gameName={gameName} resetKey={gameState}>
              <GameComponent
                gameState={gameState}
                players={players}
                localPlayerId={playerId}
                onAction={handleAction}
                isMyTurn={false}
                credits={credits}
                avatarUrl={avatarUrl ?? undefined}
                powerUpCosts={powerUpCosts}
                powerUpUsesPerMatch={powerUpUsesPerMatch}
                powerUpUsage={powerUpUsage}
                serverClockOffset={serverClockOffset}
                apiBase={API_BASE}
                // The self-contained games move loyalty points from inside their own
                // SPA. The /api/loyalty proxy injects the provider secret, so it only
                // accepts an authenticated call and pins every movement to the player
                // this token identifies — without it their wallets are read-only.
                authToken={token ?? undefined}
                onExit={() => { void navigate(-1); }}
              />
            </GameBoundary>
          </Suspense>
        </ProviderIntro>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Embed waiting lobby — full-screen dark battle layout
  // ---------------------------------------------------------------------------
  if (embed && status === 'waiting') {
    const host = players.find((p) => p.isHost);
    const opponent = players.find((p) => !p.isHost);
    const connectedCount = players.filter((p) => p.isConnected).length;
    const canStart = isHost && connectedCount >= (gameMeta?.minPlayers ?? 2);

    const PlayerCard = ({ player, label }: { player?: { id: string; displayName: string; isConnected: boolean } | null; label: string }) => {
      const isMe = player?.id === playerId;
      const initial = player?.displayName?.[0]?.toUpperCase() ?? '?';
      const colors = ['from-cyan-500 to-blue-600', 'from-violet-500 to-purple-700', 'from-pink-500 to-rose-600', 'from-amber-500 to-orange-600'];
      const colorIdx = player ? player.id.charCodeAt(0) % colors.length : 0;

      return (
        <div className={`flex-1 h-44 rounded-2xl p-4 flex flex-col items-center justify-center gap-3 ${player ? 'bg-gray-800/80 border border-gray-700' : 'bg-gray-800/40 border border-gray-700/50 border-dashed'}`}>
          {player ? (
            <>
              <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${colors[colorIdx]} flex items-center justify-center text-2xl font-black text-white shadow-lg`}>
                {initial}
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-0.5">{label}</p>
                <p className={`text-sm font-black uppercase tracking-wide ${isMe ? 'text-cyan-400' : 'text-white'}`}>
                  {player.displayName}
                </p>
                <span className="mt-1 inline-block text-[10px] font-semibold text-green-400 uppercase tracking-widest">● READY</span>
              </div>
            </>
          ) : (
            <>
              <div
                className="w-16 h-16 rounded-full bg-gray-700/50 border-2 border-dashed border-gray-600 flex items-center justify-center"
                style={{ animation: 'waiting-slot 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-0.5">{label}</p>
                <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">Waiting...</p>
              </div>
            </>
          )}
        </div>
      );
    };

    return (
      <div className="min-h-dvh flex flex-col" style={{ background: 'linear-gradient(to bottom, #060D1A, #0D1520)' }}>

        {/* Invite sheet — the share button in the top bar opens this */}
        <InviteSheet
          open={showInvite}
          onClose={() => setShowInvite(false)}
          roomId={roomId}
          gameName={gameName}
          tone="game"
        />

        {/* Chat bottom sheet */}
        <ChatBottomSheet
          showChat={showChat}
          onClose={() => { setShowChat(false); setUnreadCount(0); }}
          chatMessages={chatMessages}
          chatInput={chatInput}
          setChatInput={setChatInput}
          handleSendChat={handleSendChat}
          chatEndRef={chatEndRef}
          chatInputRef={chatInputRef}
          playerId={playerId}
        />

        {/* Chat toast — top of screen */}
        {chatToast && !showChat && (
          <div
            className="fixed bottom-24 right-4 z-40 cursor-pointer"
            onClick={() => { setShowChat(true); setUnreadCount(0); setChatToast(null); }}
          >
            <div className="flex items-center gap-2.5 rounded-2xl bg-gray-900/95 border border-gray-700 px-4 py-2.5 shadow-xl max-w-[220px]">
              <div className="flex h-6 w-6 rounded-full bg-cyan-400/10 items-center justify-center shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-bold text-cyan-400 uppercase tracking-wide truncate">{chatToast.displayName}</p>
                <p className="text-xs text-white leading-snug line-clamp-1">{chatToast.text}</p>
              </div>
            </div>
          </div>
        )}

        {/* Top bar — back + game title + share */}
        <div className="pl-[max(1.25rem,var(--safe-l))] pr-[max(1.25rem,var(--safe-r))] pt-[max(3.5rem,calc(var(--safe-t)+0.5rem))] pb-2 flex items-center gap-3">
          {/* Back — goes back in WebView/browser history (Create or Join screen) */}
          <button
            type="button"
            onClick={() => {
              socketRef.current?.destroy();
              reset();
              void navigate(-1);
            }}
            className="p-1.5 -ml-1 rounded-xl text-gray-500 hover:text-white active:scale-95 transition-all shrink-0"
            title="Go back"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </button>
          <div className="flex-1">
            <p className="text-gray-500 text-xs uppercase tracking-widest">Room {roomId}</p>
            <h1 className="text-xl font-black uppercase text-white tracking-wide leading-tight">{gameName}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowInvite(true)}
              className="p-2 rounded-xl bg-gray-800 text-gray-400 hover:text-cyan-400 active:scale-95 transition-all"
              title="Invite players"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            </button>
            {/* Chat button */}
            <button
              type="button"
              onClick={() => { setShowChat(true); setUnreadCount(0); setChatToast(null); }}
              className="relative p-2 rounded-xl bg-gray-800 text-gray-400 hover:text-cyan-400 active:scale-95 transition-all"
              title="Chat"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col px-5 pt-6 pb-4 gap-5 overflow-y-auto">

          {/* Player cards */}
          <div className="flex items-center gap-3">
            <PlayerCard player={host ?? null} label="Player 1" />
            <div className="shrink-0">
              <span className="text-2xl font-black text-gray-600 tracking-wider">VS</span>
            </div>
            <PlayerCard player={opponent ?? null} label="Player 2" />
          </div>

          {/* Dynamic middle section */}
          {!opponent ? (
            /* ── Waiting state — no opponent yet ── */
            <div className="flex flex-col items-center gap-3 py-2">
              {/* Animated waiting indicator */}
              <div className="flex items-center gap-1.5">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-1.5 w-1.5 rounded-full bg-cyan-400/60"
                    style={{ animation: `waiting-dot 1.2s ease-in-out ${i * 0.2}s infinite` }}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 text-center">
                Waiting for an opponent · Share code{' '}
                <span className="text-cyan-400 font-bold font-mono">{roomId}</span>
              </p>
            </div>
          ) : (
            /* ── Opponent joined — show a "ready to fight" label ── */
            <p className="text-center text-xs text-green-400 font-semibold uppercase tracking-widest">
              ● Both players ready
            </p>
          )}

          {/* How to play — always visible */}
          {gameMeta && gameMeta.howToPlay.length > 0 && (
            <div className="rounded-2xl border border-gray-700/60 bg-gray-800/30 px-5 py-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-3">How to play</p>
              <ul className="space-y-2.5">
                {gameMeta.howToPlay.slice(0, 3).map((rule, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-cyan-400/10 text-[10px] font-bold text-cyan-400">
                      {i + 1}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{rule}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Bottom action */}
        <div className="px-5 pb-12">
          {isHost ? (
            <button
              type="button"
              onClick={() => void handleStartGame()}
              disabled={!canStart || startingGame}
              /* Greyed out with alpha colours rather than `opacity`: an element
                 with opacity < 1 paints atomically like a positioned box, so on
                 the app WebView — where <dialog> gets no top layer — this button
                 punched up through any sheet opened over it. */
              className="w-full py-4 rounded-2xl bg-cyan-400 text-black font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2 disabled:bg-cyan-400/30 disabled:text-black/50 active:scale-[0.97] transition-all"
            >
              {startingGame ? (
                <div className="h-5 w-5 rounded-full border-2 border-black/30 border-t-black animate-spin" />
              ) : 'START GAME'}
            </button>
          ) : (
            <div className="w-full py-4 rounded-2xl bg-gray-800 text-gray-400 font-bold text-sm uppercase tracking-widest text-center">
              Waiting for host to start…
            </div>
          )}
        </div>
      </div>
    );
  } // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Embed playing layout — dark battle screen with player headers + game board
  // ---------------------------------------------------------------------------
  if (embed && status === 'playing') {
    const currentPlayerId =
      typeof gameState === 'object' && gameState !== null && 'currentPlayerId' in gameState
        ? (gameState as { currentPlayerId: string }).currentPlayerId
        : null;

    const colors = ['from-cyan-500 to-blue-600', 'from-violet-500 to-purple-700', 'from-pink-500 to-rose-600', 'from-amber-500 to-orange-600'];

    const howToPlay = gameMeta?.howToPlay ?? [];

    return (
      <div className="min-h-dvh flex flex-col" style={{ background: 'linear-gradient(to bottom, #060D1A, #0D1520)' }}>

        {/* How to play modal */}
        {showRules && howToPlay.length > 0 && (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm p-4 pb-8"
            onClick={() => setShowRules(false)}
          >
            <div
              className="w-full max-w-sm rounded-3xl px-7 pt-8 pb-7 flex flex-col gap-5"
              style={{ background: 'linear-gradient(to bottom, #0D1520, #111827)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cyan-400/10">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-black uppercase tracking-wide text-white">How to Play</h3>
                  <p className="text-[11px] text-gray-500">{gameName}</p>
                </div>
              </div>

              <ul className="space-y-4">
                {howToPlay.map((rule, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-400/10 text-[11px] font-bold text-cyan-400">
                      {i + 1}
                    </span>
                    <p className="text-sm text-gray-300 leading-relaxed">{rule}</p>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => setShowRules(false)}
                className="w-full py-3.5 rounded-2xl bg-cyan-400 text-black font-black text-sm uppercase tracking-widest active:scale-[0.97] transition-transform"
              >
                Got it
              </button>
            </div>
          </div>
        )}

        {/* Exit confirmation modal */}
        {showExitConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm p-4 pb-8"
            onClick={() => setShowExitConfirm(false)}
          >
            <div
              className="w-full max-w-sm rounded-3xl px-7 pt-8 pb-7 flex flex-col gap-5"
              style={{ background: 'linear-gradient(to bottom, #0D1520, #111827)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Icon */}
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-black uppercase tracking-wide text-white">Leave Game?</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">Your opponent is still playing. Leaving now counts as a forfeit.</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowExitConfirm(false);
                    socketRef.current?.destroy();
                    reset();
                    if (window.ReactNativeWebView) {
                      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'NAVIGATE_TO_LOBBY' }));
                    } else {
                      void navigate('/');
                    }
                  }}
                  className="w-full py-3.5 rounded-2xl bg-red-500/20 border border-red-500/30 text-red-400 font-black text-sm uppercase tracking-widest active:scale-[0.97] transition-all hover:bg-red-500/30"
                >
                  Leave Anyway
                </button>
                <button
                  type="button"
                  onClick={() => setShowExitConfirm(false)}
                  className="w-full py-3.5 rounded-2xl bg-cyan-400 text-black font-black text-sm uppercase tracking-widest active:scale-[0.97] transition-transform"
                >
                  Stay in Game
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Chat bottom sheet */}
        <ChatBottomSheet
          showChat={showChat}
          onClose={() => { setShowChat(false); setUnreadCount(0); }}
          chatMessages={chatMessages}
          chatInput={chatInput}
          setChatInput={setChatInput}
          handleSendChat={handleSendChat}
          chatEndRef={chatEndRef}
          chatInputRef={chatInputRef}
          playerId={playerId}
        />

        {/* Chat toast — bottom-right */}
        {chatToast && !showChat && (
          <div
            className="fixed bottom-24 right-4 z-40 cursor-pointer"
            onClick={() => { setShowChat(true); setUnreadCount(0); setChatToast(null); }}
          >
            <div className="flex items-center gap-2.5 rounded-2xl bg-gray-900/95 border border-gray-700 px-4 py-2.5 shadow-xl max-w-[220px]">
              <div className="flex h-6 w-6 rounded-full bg-cyan-400/10 items-center justify-center shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-bold text-cyan-400 uppercase tracking-wide truncate">{chatToast.displayName}</p>
                <p className="text-xs text-white leading-snug line-clamp-1">{chatToast.text}</p>
              </div>
            </div>
          </div>
        )}

        {/* Top bar */}
        <div className="pl-[max(1.25rem,var(--safe-l))] pr-[max(1.25rem,var(--safe-r))] pt-[max(3rem,calc(var(--safe-t)+0.5rem))] pb-3 flex items-center gap-2">
          {/* Exit to lobby */}
          <button
            type="button"
            onClick={() => setShowExitConfirm(true)}
            className="p-1.5 -ml-1 rounded-xl text-gray-600 hover:text-gray-400 active:scale-95 transition-all shrink-0"
            title="Exit to lobby"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </button>
          <div className="flex items-center gap-1.5 flex-1">
            <p className="text-gray-500 dark:text-gray-400 text-xs font-bold uppercase tracking-widest">{gameName}</p>
            {howToPlay.length > 0 && (
              <button
                onClick={() => setShowRules(true)}
                className="p-1 rounded-lg text-gray-600 hover:text-cyan-400 transition-colors active:scale-95"
                title="How to play"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
                </svg>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Chat button */}
            <button
              type="button"
              onClick={() => { setShowChat(true); setUnreadCount(0); setChatToast(null); }}
              className="relative p-1.5 rounded-xl text-gray-600 hover:text-cyan-400 active:scale-95 transition-all"
              title="Chat"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <span className="text-[10px] font-semibold text-cyan-400 uppercase tracking-widest">● Live</span>
          </div>
        </div>

        {/* Player headers — compact horizontal strip */}
        <div className="mx-5 rounded-2xl bg-gray-800/50 border border-gray-700/60 flex items-stretch overflow-hidden">
          {players.map((p, i) => {
            const isActive = p.id === currentPlayerId;
            const isMe = p.id === playerId;
            const colorIdx = p.id.charCodeAt(0) % colors.length;
            const initial = p.displayName[0]?.toUpperCase() ?? '?';
            return (
              <div
                key={p.id}
                className={`flex-1 flex items-center gap-2.5 px-4 py-3 transition-colors ${
                  isActive ? 'bg-cyan-400/8' : ''
                } ${i === 0 ? 'border-r border-gray-700/60' : ''}`}
              >
                <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${colors[colorIdx]} flex items-center justify-center text-xs font-black text-white shrink-0`}>
                  {initial}
                </div>
                <div className="min-w-0">
                  <p className={`text-xs font-black uppercase tracking-wide truncate ${isMe ? 'text-cyan-400' : 'text-white'}`}>
                    {p.displayName}{isMe ? ' (you)' : ''}
                  </p>
                  {isActive && isMe ? (
                    <p className="text-[10px] font-semibold text-green-400 uppercase tracking-widest">● Your turn</p>
                  ) : isActive && !isMe ? (
                    <p className="text-[10px] font-semibold text-amber-400 uppercase tracking-widest">● Thinking...</p>
                  ) : (
                    <p className="text-[10px] text-gray-500 uppercase tracking-widest">Waiting</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Game board */}
        <div className="flex-1 flex flex-col justify-center items-center px-2 py-4 text-white">
          {GameComponent ? (
            <Suspense fallback={
              <div className="flex items-center justify-center py-16">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-cyan-400/30 border-t-cyan-400" />
              </div>
            }>
              <GameBoundary gameName={gameName} resetKey={gameState}>
                <GameComponent
                  gameState={gameState}
                  players={players}
                  localPlayerId={playerId}
                  onAction={handleAction}
                  isMyTurn={currentPlayerId === playerId}
                  credits={credits}
                  powerUpCosts={powerUpCosts}
                  powerUpUsesPerMatch={powerUpUsesPerMatch}
                  powerUpUsage={powerUpUsage}
                  serverClockOffset={serverClockOffset}
                />
              </GameBoundary>
            </Suspense>
          ) : (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-cyan-400/30 border-t-cyan-400" />
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Embed game over layout
  // ---------------------------------------------------------------------------
  if (embed && status === 'finished') {
    const colors = ['from-cyan-500 to-blue-600', 'from-violet-500 to-purple-700', 'from-pink-500 to-rose-600', 'from-amber-500 to-orange-600'];

    // Result config per outcome
    // `glow` must be a complete, literal class string: Tailwind scans source
    // text, so a class assembled at runtime (`${glowColor}/40`) is never
    // emitted into the stylesheet.
    const result = isDraw
      ? { label: "IT'S A DRAW", sublabel: 'Well fought, both sides', color: 'text-gray-300', glow: 'bg-gray-400 shadow-[0_0_12px_4px] shadow-gray-400/40' }
      : iWon
      ? { label: 'YOU WON!', sublabel: gameOverReason ?? 'Victory is yours', color: 'text-cyan-400', glow: 'bg-cyan-400 shadow-[0_0_12px_4px] shadow-cyan-400/40' }
      : { label: 'DEFEATED', sublabel: gameOverReason ?? 'Better luck next time', color: 'text-orange-400', glow: 'bg-orange-400 shadow-[0_0_12px_4px] shadow-orange-400/40' };

    return (
      <div className="min-h-dvh flex flex-col" style={{ background: 'linear-gradient(to bottom, #060D1A, #0D1520)' }}>

        {/* Chat bottom sheet */}
        <ChatBottomSheet
          showChat={showChat}
          onClose={() => { setShowChat(false); setUnreadCount(0); }}
          chatMessages={chatMessages}
          chatInput={chatInput}
          setChatInput={setChatInput}
          handleSendChat={handleSendChat}
          chatEndRef={chatEndRef}
          chatInputRef={chatInputRef}
          playerId={playerId}
        />

        {/* Chat toast — top of screen */}
        {chatToast && !showChat && (
          <div
            className="fixed bottom-24 right-4 z-40 cursor-pointer"
            onClick={() => { setShowChat(true); setUnreadCount(0); setChatToast(null); }}
          >
            <div className="flex items-center gap-2.5 rounded-2xl bg-gray-900/95 border border-gray-700 px-4 py-2.5 shadow-xl max-w-[220px]">
              <div className="flex h-6 w-6 rounded-full bg-cyan-400/10 items-center justify-center shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-bold text-cyan-400 uppercase tracking-wide truncate">{chatToast.displayName}</p>
                <p className="text-xs text-white leading-snug line-clamp-1">{chatToast.text}</p>
              </div>
            </div>
          </div>
        )}

        {/* Top bar */}
        <div className="pl-[max(1.25rem,var(--safe-l))] pr-[max(1.25rem,var(--safe-r))] pt-[max(3rem,calc(var(--safe-t)+0.5rem))] pb-2 flex items-center justify-between">
          <p className="text-gray-500 text-xs font-bold uppercase tracking-widest">{gameName}</p>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest">Match Over</span>
            <button
              type="button"
              onClick={() => { setShowChat(true); setUnreadCount(0); setChatToast(null); }}
              className="relative p-1.5 rounded-xl bg-gray-800 text-gray-400 hover:text-cyan-400 active:scale-95 transition-all"
              title="Chat"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="flex-1 flex flex-col px-5 py-4 gap-5 overflow-y-auto">

          {/* Result hero */}
          <div className="flex flex-col items-center pt-4 pb-2 gap-3">
            {/* Glow dot */}
            <div aria-hidden="true" className={`h-2 w-2 rounded-full ${result.glow}`} />
            <h1 className={`text-4xl font-black uppercase tracking-tight ${result.color}`}>
              {result.label}
            </h1>
            <p className="text-xs text-gray-500 uppercase tracking-widest">{result.sublabel}</p>
          </div>

          {/* Credits earned — hero moment */}
          {creditsEarned !== null && creditsEarned > 0 && (
            <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/5 px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-[11px] text-gray-500 uppercase tracking-widest mb-1">Credits Earned</p>
                <p className="text-3xl font-black text-cyan-400 tracking-tight">
                  +{creditsEarned}
                  <span className="text-base font-semibold text-gray-500 dark:text-gray-400 ml-1.5">cr</span>
                </p>
              </div>
              <div className="h-12 w-12 rounded-2xl bg-cyan-400/10 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
                </svg>
              </div>
            </div>
          )}

          {/* Scoreboard */}
          {players.length > 0 && (
            <div className="rounded-2xl border border-gray-700/60 bg-gray-800/30 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-700/60">
                <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">Final Result</p>
              </div>
              <div className="divide-y divide-gray-700/40">
                {players.map((p) => {
                  const isWinner = winners.includes(p.id);
                  const isMe = p.id === playerId;
                  const colorIdx = p.id.charCodeAt(0) % colors.length;
                  const initial = p.displayName[0]?.toUpperCase() ?? '?';
                  const playerScore = scores[p.id];
                  return (
                    <div key={p.id} className="flex items-center gap-3 px-5 py-3">
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${colors[colorIdx]} flex items-center justify-center text-xs font-black text-white shrink-0`}>
                        {initial}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold truncate ${isMe ? 'text-cyan-400' : 'text-white'}`}>
                          {p.displayName}{isMe ? ' (you)' : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {playerScore !== undefined && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{playerScore} pts</span>
                        )}
                        {isDraw ? (
                          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 bg-gray-700/60 rounded-full px-2.5 py-0.5">Draw</span>
                        ) : isWinner ? (
                          <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-400 bg-cyan-400/10 rounded-full px-2.5 py-0.5">Winner</span>
                        ) : (
                          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 bg-gray-800/60 rounded-full px-2.5 py-0.5">Defeated</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Head-to-head stats */}
          {h2hRows.length > 0 && (
            <div className="rounded-2xl border border-gray-700/60 bg-gray-800/30 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-700/60">
                <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">Head-to-Head · All Time</p>
              </div>
              <div className="px-5 py-1">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700/40">
                      <th className="py-2 text-left text-[11px] font-medium text-gray-500">Player</th>
                      <th className="py-2 text-center text-[11px] font-medium text-green-400">W</th>
                      <th className="py-2 text-center text-[11px] font-medium text-red-400">L</th>
                      <th className="py-2 text-center text-[11px] font-medium text-gray-500">D</th>
                    </tr>
                  </thead>
                  <tbody>
                    {h2hRows
                      .slice()
                      .sort((a, b) => b.wins - a.wins || a.losses - b.losses)
                      .map((row) => {
                        const isMe = row.playerId === playerId;
                        return (
                          <tr key={row.playerId} className="border-b border-gray-700/20 last:border-0">
                            <td className={`py-2.5 text-sm font-semibold truncate max-w-[120px] ${isMe ? 'text-cyan-400' : 'text-white'}`}>
                              {row.displayName}{isMe ? ' (you)' : ''}
                            </td>
                            <td className="py-2.5 text-center text-sm font-bold text-green-400">{row.wins}</td>
                            <td className="py-2.5 text-center text-sm font-bold text-red-400">{row.losses}</td>
                            <td className="py-2.5 text-center text-sm font-bold text-gray-500">{row.draws}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Rematch nudge for non-host */}
          {!isHost && (
            <div className={`rounded-2xl px-5 py-4 border ${rematchRoomId ? 'border-cyan-400/30 bg-cyan-400/5' : 'border-gray-700/60 bg-gray-800/30'}`}>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                {rematchRoomId ? '🎮 Host started a rematch — ready to go?' : 'Waiting for the host to start a rematch…'}
              </p>
              <button
                type="button"
                onClick={() => void handleJoinRematch()}
                disabled={!rematchRoomId || joiningRematch}
                className="w-full py-3.5 rounded-xl bg-cyan-400 text-black font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2 disabled:bg-cyan-400/25 disabled:text-black/40 active:scale-[0.97] transition-all"
              >
                {joiningRematch ? (
                  <div className="h-5 w-5 rounded-full border-2 border-black/30 border-t-black animate-spin" />
                ) : 'Join Rematch'}
              </button>
            </div>
          )}
        </div>

        {/* Bottom actions */}
        <div className="px-5 pb-10 pt-2 flex flex-col gap-3">
          {isHost && (
            <button
              type="button"
              onClick={() => void handleRematch()}
              disabled={rematchLoading}
              className="w-full py-4 rounded-2xl bg-cyan-400 text-black font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2 disabled:bg-cyan-400/40 disabled:text-black/60 active:scale-[0.97] transition-all"
            >
              {rematchLoading ? (
                <div className="h-5 w-5 rounded-full border-2 border-black/30 border-t-black animate-spin" />
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>
                  </svg>
                  Play Again
                </>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              // Clean up socket and room state, then hand navigation back to the native app
              socketRef.current?.destroy();
              reset();
              if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'NAVIGATE_TO_LOBBY' }));
              } else {
                void navigate('/');
              }
            }}
            className="w-full py-3.5 rounded-2xl border border-gray-700 text-gray-500 dark:text-gray-400 font-bold text-sm uppercase tracking-widest text-center active:scale-[0.97] transition-all hover:border-gray-500 hover:text-gray-300"
          >
            Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  void userGameReady; // consumed so eslint doesn't warn about unused state

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <main className="mx-auto max-w-3xl px-4 pt-3 pb-8">
      {/* Invite sheet — the share button in the room info row opens this */}
      <InviteSheet
        open={showInvite}
        onClose={() => setShowInvite(false)}
        roomId={roomId}
        gameName={gameName}
        tone="app"
      />
      {/* Details Modal */}
      {gameId && (
        <DetailsModal
          open={showDetails}
          gameId={gameId}
          gameName={gameName}
          isHost={isHost}
          gameOptions={gameOptions}
          onOptionsChange={setGameOptions}
          onClose={() => setShowDetails(false)}
        />
      )}
      {/* Leave confirmation — desktop parity with the embed exit flow. */}
      <Modal
        open={showExitConfirm}
        onClose={() => setShowExitConfirm(false)}
        title="Leave game"
        panelClassName="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-800"
      >
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Leave this game?</h3>
        <p className="mt-1 text-sm text-pretty text-gray-500 dark:text-gray-400">
          {status === 'playing'
            ? 'The game is still in progress. Leaving now counts as a forfeit.'
            : 'You can rejoin with the room code afterwards.'}
        </p>
        <div className="mt-5 flex gap-3">
          <Button variant="ghost" fullWidth onClick={() => setShowExitConfirm(false)}>
            Stay
          </Button>
          <Button
            variant="primary"
            fullWidth
            onClick={() => { setShowExitConfirm(false); handleLeave(); }}
            className="bg-red-600 hover:bg-red-700 active:bg-red-800 focus-visible:ring-red-500"
          >
            Leave
          </Button>
        </div>
      </Modal>

      {/* Fullscreen game overlay */}
      {isFullscreen && GameComponent && (
        <div className="fixed inset-0 z-50 flex flex-col bg-gray-900">
          <div className="flex items-center justify-between pl-[max(1rem,var(--safe-l))] pr-[max(1rem,var(--safe-r))] pt-[calc(0.5rem+var(--safe-t))] pb-2 bg-gray-800 border-b border-gray-700">
            <span className="text-sm font-bold text-white">{gameName}</span>
            <button
              onClick={() => setIsFullscreen(false)}
              className="rounded-lg p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-700 hover:text-white transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-auto">
            <Suspense fallback={<div className="flex items-center justify-center h-full"><div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" /></div>}>
              <GameBoundary gameName={gameName} resetKey={gameState}>
                <GameComponent
                  gameState={gameState}
                  players={players}
                  localPlayerId={playerId}
                  onAction={handleAction}
                  isMyTurn={
                    typeof gameState === 'object' && gameState !== null && 'currentPlayerId' in gameState &&
                    (gameState as { currentPlayerId: string }).currentPlayerId === playerId
                  }
                  credits={credits}
                  powerUpCosts={powerUpCosts}
                  powerUpUsesPerMatch={powerUpUsesPerMatch}
                  powerUpUsage={powerUpUsage}
                  serverClockOffset={serverClockOffset}
                />
              </GameBoundary>
            </Suspense>
          </div>
        </div>
      )}

      {/* Game name — hidden in embed mode (native nav bar shows context) */}
      {!embed && (
        <div className="mb-1 flex items-center gap-2">
          <img src="/favicon.png" alt="" className="w-6 h-6 rounded-md flex-shrink-0" />
          <h1 className="text-2xl font-black text-gray-900 dark:text-gray-100 leading-tight">{gameName}</h1>
          {gameId && (
            <button
              type="button"
              onClick={() => toggleFavorite(gameId)}
              title={favorites.includes(gameId) ? 'Remove from favorites' : 'Add to favorites'}
              className="ml-1 text-xl leading-none transition-transform hover:scale-110 active:scale-95"
            >
              {favorites.includes(gameId) ? '❤️' : '🤍'}
            </button>
          )}
        </div>
      )}

      {/* Room info row */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {/* Room code — visible in both modes; in embed the native share button handles invites */}
          {!embed && (
            <button
              type="button"
              aria-label={`Room code ${roomId}. Copy to clipboard.`}
              onClick={() => {
                void copyText(roomId).then((ok) => {
                  if (!ok) return;
                  setCodeCopied(true);
                  setTimeout(() => setCodeCopied(false), 2000);
                });
              }}
              className="rounded-md bg-gray-100 px-2 py-0.5 font-mono text-sm font-semibold text-gray-700 transition-colors hover:bg-indigo-100 hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-indigo-900/40 dark:hover:text-indigo-300"
            >
              {codeCopied ? '✓ Copied' : roomId}
            </button>
          )}
          <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium capitalize text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400">
            {status}
          </span>
          {/* Invites open the sheet, which offers the host app's native share
              sheet first. Outside embed the room-code button above is the
              share affordance. */}
          {embed && (
            <button
              type="button"
              onClick={() => setShowInvite(true)}
              title="Invite players"
              className="rounded-lg p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-indigo-600 transition-colors dark:hover:bg-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
            </button>
          )}
          {gameId && (
            <button
              onClick={() => setShowDetails(true)}
              title="Game details"
              className="rounded-lg p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-indigo-600 transition-colors dark:hover:bg-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
              </svg>
            </button>
          )}
          {/* Chat button */}
          <button
            onClick={() => { setChatOpen((o) => !o); setUnreadCount(0); clearBadge(); }}
            title="Chat"
            className="relative rounded-lg p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-indigo-600 transition-colors dark:hover:bg-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className={['flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', connected ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : gaveUp ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'].join(' ')}>
            <span className={['h-1.5 w-1.5 rounded-full', connected ? 'bg-green-500' : gaveUp ? 'bg-red-500' : 'bg-amber-500'].join(' ')} />
            {connected ? 'Live' : gaveUp ? 'Offline' : 'Connecting…'}
          </span>
          <Button
            variant="ghost"
            onClick={() => (status === 'playing' ? setShowExitConfirm(true) : handleLeave())}
          >
            Leave
          </Button>
        </div>
      </div>

      {/* Give-up banner */}
      {gaveUp && (
        <div className="mb-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          Connection lost and could not reconnect. Please refresh the page or{' '}
          <button onClick={handleLeave} className="underline">return to the lobby</button>.
        </div>
      )}

      {/* Players */}
      {SOLO_GAMES.has(gameId ?? '') ? (
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <span>👥</span>
          <span>{players.filter((p) => p.isConnected).length} player{players.filter((p) => p.isConnected).length !== 1 ? 's' : ''} online</span>
        </div>
      ) : (
        <Card className="mb-4">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setPlayersOpen((o) => !o)}
              className="flex items-center gap-1.5 text-left"
            >
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Players</p>
              <span className="text-xs text-gray-500 dark:text-gray-400">({players.length})</span>
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-3.5 w-3.5 text-gray-400 transition-transform ${playersOpen ? '' : '-rotate-90'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {isHost && status === 'waiting' && (
              <Button variant="primary" onClick={() => void handleStartGame()} loading={startingGame} disabled={players.filter((p) => p.isConnected).length < (gameMeta?.minPlayers ?? 2)}>
                Start Game
              </Button>
            )}
          </div>
          {playersOpen && (
            <>
              <ul className="mt-2 divide-y divide-gray-100 dark:divide-gray-700">
                {players.map((player) => (
                  <li key={player.id} className="flex items-center gap-2 py-2">
                    <Avatar displayName={player.displayName} playerId={player.id} online={player.isConnected} size="sm" />
                    <div className="flex-1 min-w-0 flex items-center gap-1.5">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {player.displayName}
                        {player.id === playerId && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">(you)</span>}
                      </p>
                      {player.isHost && <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400">Host</span>}
                      {!player.isConnected && <span className="text-xs text-gray-500 dark:text-gray-400">· offline</span>}
                    </div>
                  </li>
                ))}
                {players.length === 0 && (
                  <li className="py-3 text-center text-sm text-gray-500 dark:text-gray-400">Waiting for players to join…</li>
                )}
              </ul>
              {status === 'waiting' && !isHost && (
                <p className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">Waiting for the host to start…</p>
              )}
            </>
          )}
        </Card>
      )}

      {/* Game area */}
      {status === 'playing' && GameComponent && (
        <Card className="mb-4">
          {!embed && gameMeta?.supportsFullscreen && (
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setIsFullscreen(true)}
                title="Fullscreen"
                className="rounded-lg p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-indigo-600 transition-colors dark:hover:bg-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
              </button>
            </div>
          )}
          <Suspense fallback={
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
            </div>
          }>
            <GameBoundary gameName={gameName} resetKey={gameState}>
              <GameComponent
                gameState={gameState}
                players={players}
                localPlayerId={playerId}
                onAction={handleAction}
                isMyTurn={
                  typeof gameState === 'object' &&
                  gameState !== null &&
                  'currentPlayerId' in gameState &&
                  (gameState as { currentPlayerId: string }).currentPlayerId === playerId
                }
                credits={credits}
                powerUpCosts={powerUpCosts}
                powerUpUsesPerMatch={powerUpUsesPerMatch}
                powerUpUsage={powerUpUsage}
                serverClockOffset={serverClockOffset}
              />
            </GameBoundary>
          </Suspense>
        </Card>
      )}

      {status === 'playing' && !GameComponent && (
        <Card className="mb-4">
          {!userGameReady ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
            </div>
          ) : (
            <p className="py-8 text-center text-gray-500">
              Game component not found for: <code className="rounded bg-gray-100 px-1 py-0.5 text-sm">{gameId}</code>
            </p>
          )}
        </Card>
      )}

      {/* Game over */}
      {status === 'finished' && (
        <Card className="mb-4">
          <div className="py-6 text-center">
            {isDraw ? (
              <>
                <p className="text-4xl">🤝</p>
                <p className="mt-2 text-2xl font-bold text-gray-700 dark:text-gray-300">It's a Draw!</p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{gameOverReason}</p>
              </>
            ) : iWon ? (
              <>
                <p className="text-4xl">🏆</p>
                <p className="mt-2 text-2xl font-bold text-green-600">You Won!</p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{gameOverReason}</p>
              </>
            ) : winnerPlayers.length > 0 ? (
              <>
                <p className="text-4xl">🎉</p>
                <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {winnerPlayers.map((p) => p.displayName).join(' & ')} won!
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{gameOverReason}</p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">Game Over!</p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{gameOverReason}</p>
              </>
            )}

            {/* Credits earned */}
            {creditsEarned !== null && creditsEarned > 0 && (
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-yellow-50 px-4 py-1.5 dark:bg-yellow-900/20">
                <span className="text-base">💰</span>
                <span className="text-sm font-bold text-yellow-700 dark:text-yellow-400">+{creditsEarned} credits earned</span>
              </div>
            )}

            {/* Final scores — shown for games that track per-player scores (e.g. RPS) */}
            {Object.keys(scores).length > 0 && (
              <div className="mt-4 w-full max-w-xs mx-auto text-left">
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 text-center">Final Scores</p>
                <div className="space-y-1">
                  {[...players]
                    .sort((a, b) => (scores[b.id] ?? 0) - (scores[a.id] ?? 0))
                    .map((p, rank) => (
                      <div key={p.id} className="flex items-center gap-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 px-3 py-2">
                        <span className="w-5 text-center text-xs font-bold text-gray-500 dark:text-gray-400">
                          {rank === 0 ? '🥇' : rank === 1 ? '🥈' : rank === 2 ? '🥉' : `${rank + 1}.`}
                        </span>
                        <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                          {p.displayName}{p.id === playerId ? ' (you)' : ''}
                        </span>
                        <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{scores[p.id] ?? 0} pts</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Rematch offer for non-host — visible immediately; enabled once host creates rematch */}
            {!isHost && (
              <div className="mt-5 rounded-lg bg-indigo-50 px-4 py-3 dark:bg-indigo-900/20">
                <p className="mb-3 text-sm font-medium text-indigo-800 dark:text-indigo-300">
                  {rematchRoomId ? 'The host started a rematch!' : 'Waiting for the host to start a rematch…'}
                </p>
                <Button
                  variant="primary"
                  onClick={() => void handleJoinRematch()}
                  loading={joiningRematch}
                  disabled={!rematchRoomId}
                >
                  {rematchRoomId ? 'Join Rematch' : 'Waiting for host…'}
                </Button>
              </div>
            )}

            <div className="mt-6 flex justify-center gap-3">
              {isHost && (
                <Button variant="primary" onClick={() => void handleRematch()} loading={rematchLoading}>
                  Play Again
                </Button>
              )}
              <Button variant="ghost" onClick={handleLeave}>Lobby</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Leaderboard / session stats */}
      {SOLO_GAMES.has(gameId ?? '')
        ? (gameState as { phase?: string } | null)?.phase !== 'rising'
          ? <SessionStats gameState={gameState} />
          : null
        : <GroupLeaderboard roomId={roomId} playerCount={players.length} status={status} />
      }

      {/* Chat panel — fixed bottom overlay */}
      {chatOpen && (
        <div className="fixed bottom-0 left-0 right-0 z-40 flex flex-col bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-2xl" style={{ height: '50vh' }}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 shrink-0">
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">Chat</h3>
            <button
              onClick={() => setChatOpen(false)}
              className="flex h-6 w-6 items-center justify-center rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
            {chatMessages.length === 0 && (
              <p className="text-center text-xs text-gray-500 dark:text-gray-400 pt-4">No messages yet. Say hi! 👋</p>
            )}
            {chatMessages.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.playerId === playerId ? 'items-end' : 'items-start'}`}>
                <span className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">{m.displayName}</span>
                <div className={`max-w-[75%] rounded-2xl px-3 py-1.5 text-sm ${m.playerId === playerId ? 'bg-indigo-600 text-white rounded-tr-sm' : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-tl-sm'}`}>
                  {m.text}
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={handleSendChat} className="flex gap-2 px-4 py-3 border-t border-gray-100 dark:border-gray-700 shrink-0">
            <input
              ref={chatInputRef}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              maxLength={300}
              placeholder="Type a message…"
              className="flex-1 rounded-full border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-4 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-gray-100 dark:placeholder-gray-500"
            />
            <button
              type="submit"
              disabled={!chatInput.trim()}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </form>
        </div>
      )}

      {/* Chat toast — new message when chat is closed */}
      {chatToast && !chatOpen && (
        <div
          className="fixed bottom-20 right-4 z-50 cursor-pointer rounded-xl bg-gray-900 px-4 py-3 shadow-xl max-w-[220px]"
          onClick={() => { setChatOpen(true); setUnreadCount(0); setChatToast(null); clearBadge(); }}
        >
          <p className="text-[11px] font-bold text-indigo-300 mb-0.5">{chatToast.displayName}</p>
          <p className="text-sm text-white leading-snug line-clamp-2">{chatToast.text}</p>
        </div>
      )}

    </main>
  );
}
